import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { mapRefDataPickList } from 'shared/utils/Helpers';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';

export const VehicleHoistDesignDetails = React.forwardRef((props, ref) => {
  const {
    driveType: savedDriveType = '',
    otherDriveType: savedOtherDriveType = '',
    maxHoistingSpeed: savedMaxHoistingSpeed = '',
    maxTravel: savedMaxTravel = '',
    maxRatedCapacity: savedMaxRatedCapacity = '',
  } = props.value || {};

  const [driveType, setDriveType] = useState(savedDriveType);
  const [otherDriveType, setOtherDriveType] = useState(savedOtherDriveType);
  const [maxRatedCapacity, setMaxRatedCapacity] = useState(savedMaxRatedCapacity);
  const [maxTravel, setMaxTravel] = useState(savedMaxTravel);
  const [maxHoistingSpeed, setMaxHoistingSpeed] = useState(savedMaxHoistingSpeed);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const REF_CONSTANTS = PlantDesignRefData.vehicleHoist;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.vehicleHoist;

  const driveTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.driveTypeOptions]);

  const validators = [
    {
      fieldName: 'driveType',
      fieldValue: driveType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'driveType_other',
      fieldValue: otherDriveType,
      isValid: (v) => !isEmpty(v) || driveType !== 'Other (please describe)',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'maxRatedCapacity',
      fieldValue: maxRatedCapacity,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxTravel',
      fieldValue: maxTravel,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxHoistingSpeed',
      fieldValue: maxHoistingSpeed,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        const isValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isValid);
        return isValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      driveType,
      otherDriveType,
      maxRatedCapacity,
      maxTravel,
      maxHoistingSpeed,
    };

    if (driveType !== 'Other (please describe)') delete details.otherDriveType;

    props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [driveType, otherDriveType, maxRatedCapacity, maxTravel, maxHoistingSpeed]);

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <RadioOrDropdownSelector
        id="driveType"
        name="driveType"
        options={driveTypeOptions}
        title={PAGE_CONSTANTS.driveType.label}
        errorMessage={errors?.driveType?.errorMessage}
        hasError={showErrors && errors?.driveType?.invalid}
        onChange={(v) => {
          setDriveType(v);
          setOtherDriveType('');
        }}
        value={driveType}
        showExtraInput={driveType === 'Other (please describe)'}
        onChangeExtraInput={setOtherDriveType}
        extraInputValue={otherDriveType}
        extraInputErrorMessage={errors?.driveType_other?.errorMessage}
        extraInputHasError={showErrors && errors?.driveType_other?.invalid}
        extraInputTitle={'Please describe the drive type'}
        margin={{ top: 'lg' }}
        maxOptionsLengthForRadio={2}
        extraInputType="textarea"
      />

      <FormGroup
        id="maxRatedCapacity"
        label={PAGE_CONSTANTS.maxRatedCapacity.label}
        isRequired
        errorMessage={errors?.maxRatedCapacity?.errorMessage}
        hasError={showErrors && errors?.maxRatedCapacity?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxRatedCapacity.suffix}
          onChange={(e) => setMaxRatedCapacity(e.target.value)}
          value={maxRatedCapacity}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="maxTravel"
        label={PAGE_CONSTANTS.maxTravel.label}
        isRequired
        errorMessage={errors?.maxTravel?.errorMessage}
        hasError={showErrors && errors?.maxTravel?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxTravel.suffix}
          onChange={(e) => setMaxTravel(e.target.value)}
          value={maxTravel}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="maxHoistingSpeed"
        label={PAGE_CONSTANTS.maxHoistingSpeed.label}
        isRequired
        errorMessage={errors?.maxHoistingSpeed?.errorMessage}
        hasError={showErrors && errors?.maxHoistingSpeed?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxHoistingSpeed.suffix}
          onChange={(e) => setMaxHoistingSpeed(e.target.value)}
          value={maxHoistingSpeed}
          inputWidth="md"
        />
      </FormGroup>
    </>
  );
});

VehicleHoistDesignDetails.displayName = 'VehicleHoistDesignDetails';

VehicleHoistDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
